import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, from, switchMap, throwError } from "rxjs";
import { AuthApi } from "../../api/auth.api";
import { AuthStore } from "../../store/auth.store";
import { App } from "../app/app.service";
import { Router } from "../router/router";

const UNAUTHORIZED_LOGIN = 401;

const NO_AUTH_HEADER_URLS = [
	"/login",
	"/auth/refresh",
	"/auth/forgot-password",
	"/auth/reset-password",
];

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
	private refreshing: Promise<boolean> | undefined;

	constructor(
		private readonly authStore: AuthStore,
		private readonly authApi: AuthApi,
		private readonly router: Router,
		private readonly app: App,
	) {}

	intercept(
		request: HttpRequest<unknown>,
		next: HttpHandler,
	): Observable<HttpEvent<unknown>> {
		if (NO_AUTH_HEADER_URLS.some((url) => request.url.includes(url)))
			return next.handle(request);

		return next
			.handle(this.addJwtHeader(request))
			.pipe(
				catchError((error: HttpErrorResponse) =>
					this.refreshAndRetry(error, request, next),
				),
			);
	}

	private refreshAndRetry(
		error: HttpErrorResponse,
		request: HttpRequest<unknown>,
		next: HttpHandler,
	): Observable<HttpEvent<unknown>> {
		if (!this.isAuthError(error)) return throwError(() => error);

		const auth = this.authStore.syncMe();
		if (!auth) return throwError(() => error);

		return from(this.refreshSession(auth.refreshToken)).pipe(
			switchMap((refreshed) =>
				refreshed ? this.retry(request, next) : throwError(() => error),
			),
		);
	}

	private retry(
		request: HttpRequest<unknown>,
		next: HttpHandler,
	): Observable<HttpEvent<unknown>> {
		return next.handle(this.addJwtHeader(request)).pipe(
			catchError((retryError: HttpErrorResponse) => {
				if (retryError.status !== UNAUTHORIZED_LOGIN)
					return throwError(() => retryError);

				return from(this.closeSession()).pipe(
					switchMap(() => throwError(() => retryError)),
				);
			}),
		);
	}

	/**
	 * Shares one in-flight refresh between every request failing at the same
	 * time. Resolves true when the session was renewed; when it fails the
	 * session has already been closed (AuthApi.refreshToken logs out) and the
	 * user is sent to the login page once.
	 */
	private refreshSession(refreshToken: string): Promise<boolean> {
		this.refreshing ??= this.authApi
			.refreshToken({ refreshToken })
			.then(() => true)
			.catch(() => {
				this.router.goToLogin();
				return false;
			})
			.finally(() => {
				this.refreshing = undefined;
			});

		return this.refreshing;
	}

	/**
	 * The renewed token was rejected too: the session is unusable, so it is
	 * closed before the single navigation to the login page.
	 */
	private async closeSession(): Promise<void> {
		await this.authApi.logout();
		await this.router.goToLogin();
	}

	private isAuthError(error: HttpErrorResponse): boolean {
		return error.status === UNAUTHORIZED_LOGIN;
	}

	private addJwtHeader(request: HttpRequest<unknown>): HttpRequest<unknown> {
		const auth = this.authStore.syncMe();
		if (!auth?.accessToken) return request;

		return request.clone({
			setHeaders: {
				Authorization: `Bearer ${auth.accessToken}`,
			},
		});
	}
}
